import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, UserRound, LogOut, LockKeyhole, ClipboardList, Boxes, Award } from "lucide-react";
import { NAV_LINKS, CALENDLY_URL } from "../lib/site";
import { useClientAuth } from "../lib/auth";

const CLIENT_TOOLS = [
  { to: "/checklist", label: "Inventory Checklist", icon: ClipboardList },
  { to: "/equipment", label: "Equipment Guide", icon: Boxes },
  { to: "/certification", label: "Facilitator Certification", icon: Award },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { client, logout } = useClientAuth();

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
    setClientOpen(false);
  }, [pathname]);

  const signOut = async () => {
    await logout();
    navigate("/");
  };

  // Lock scroll when menu open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b-2 border-[#1A1A1A]"
      >
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="brand-logo-link"
            className="flex items-center gap-2 font-display font-black text-lg md:text-xl tracking-tight"
          >
            <span
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border-2 border-[#1A1A1A] bg-[#FFD700] nb-shadow-sm font-black"
              aria-hidden
            >
              TS
            </span>
            <span className="leading-none">
              <span className="block">TinkerShift</span>
              <span className="block text-[10px] md:text-xs font-mono font-medium text-[#4A4A4A] tracking-wide">
                powered by TinkerTank
              </span>
            </span>
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            {client ? (
              <div className="relative z-50">
                <button
                  type="button"
                  onClick={() => setClientOpen((v) => !v)}
                  data-testid="client-menu-toggle"
                  className="inline-flex items-center gap-1.5 bg-[#00C2B8] text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold uppercase tracking-wide nb-shadow-sm nb-press"
                >
                  <UserRound size={14} strokeWidth={2.5} />
                  Portal
                </button>
                {clientOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setClientOpen(false)}
                      aria-hidden
                    />
                    <div
                      data-testid="client-menu"
                      className="absolute right-0 top-full mt-2 z-50 w-64 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-2xl p-2 nb-shadow"
                    >
                      <p className="px-3 pt-2 pb-1 font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
                        Client tools · {(client.name || "Client").split(" ")[0]}
                      </p>
                      {CLIENT_TOOLS.map((t) => (
                        <Link
                          key={t.to}
                          to={t.to}
                          data-testid={`client-tool-${t.to.slice(1)}`}
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-bold hover:bg-[#F8F9FA] transition-colors"
                        >
                          <t.icon size={15} strokeWidth={2.5} className="text-[#0055FF]" />
                          {t.label}
                        </Link>
                      ))}
                      <button
                        type="button"
                        onClick={signOut}
                        data-testid="client-signout-btn"
                        className="mt-1 w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-bold text-[#FF007F] hover:bg-[#FF007F]/10 border-t-2 border-[#1A1A1A]/10 transition-colors"
                      >
                        <LogOut size={15} strokeWidth={2.5} /> Log out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="header-login-btn"
                className="hidden sm:inline-flex items-center gap-1.5 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold uppercase tracking-wide nb-shadow-sm nb-press hover:bg-[#FFD700] transition-colors"
              >
                <LockKeyhole size={14} strokeWidth={2.5} />
                Login
              </Link>
            )}
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-call-btn"
              className="hidden xs:inline-flex sm:inline-flex items-center gap-1.5 bg-[#0055FF] text-white border-2 border-[#1A1A1A] rounded-full px-3.5 sm:px-4 py-1.5 sm:py-2 text-xs font-bold uppercase tracking-wide nb-shadow-sm nb-press"
            >
              Book a Call <ArrowUpRight size={14} strokeWidth={2.5} />
            </a>
            <button
              type="button"
              aria-label="Toggle menu"
              aria-expanded={open}
              data-testid="menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center justify-center h-10 w-10 rounded-xl border-2 border-[#1A1A1A] bg-white nb-shadow-sm"
            >
              {open ? <X size={20} strokeWidth={2.5} /> : <Menu size={20} strokeWidth={2.5} />}
            </button>
          </div>
        </div>
      </header>

      {/* Full-screen menu overlay */}
      <div
        aria-hidden={!open}
        data-testid="nav-overlay"
        className={`fixed inset-0 z-40 bg-[#1A1A1A] text-white transition-opacity duration-300 ${
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-dots-light opacity-20 pointer-events-none" />
        <div className="relative h-full flex flex-col pt-24 md:pt-28 pb-10 overflow-y-auto">
          <nav
            className="mx-auto w-full max-w-7xl px-5 md:px-10 lg:px-12"
            data-testid="primary-nav"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#FFD700] mb-5">
              Menu · navigate
            </p>
            <ul className="divide-y divide-white/15 border-y-2 border-white/15">
              {NAV_LINKS.map((l, i) => (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    style={{
                      transitionDelay: open ? `${80 + i * 35}ms` : "0ms",
                    }}
                    className={({ isActive }) =>
                      `group flex items-center justify-between py-5 md:py-6 font-display font-black tracking-tighter text-3xl md:text-5xl lg:text-6xl leading-none transition-all duration-500 ${
                        open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                      } ${
                        isActive
                          ? "text-[#FFD700]"
                          : "text-white hover:text-[#00C2B8]"
                      }`
                    }
                  >
                    <span className="flex items-baseline gap-4 md:gap-6">
                      <span className="font-mono text-xs md:text-sm tracking-widest text-white/40">
                        0{i + 1}
                      </span>
                      {l.label}
                    </span>
                    <ArrowUpRight
                      size={28}
                      strokeWidth={2.5}
                      className="opacity-0 -translate-x-4 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                    />
                  </NavLink>
                  {l.children && (
                    <ul className="pl-10 md:pl-14 pb-4 flex flex-wrap gap-x-5 gap-y-2">
                      {l.children.map((c) => (
                        <li key={c.to}>
                          <NavLink
                            to={c.to}
                            data-testid={`nav-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                              `font-mono text-[11px] md:text-xs uppercase tracking-widest transition-colors ${
                                isActive
                                  ? "text-[#FFD700]"
                                  : "text-white/70 hover:text-[#00C2B8]"
                              }`
                            }
                          >
                            → {c.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="menu-book-call-btn"
                className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#FFD700] rounded-full px-6 py-3 font-bold uppercase text-sm tracking-wide nb-press"
              >
                Book a Discovery Call{" "}
                <ArrowUpRight size={16} strokeWidth={2.5} />
              </a>
              <div className="flex items-center gap-4 text-xs font-mono uppercase tracking-widest text-white/60">
                <span>hello@tinkertank.rocks</span>
                <span className="hidden sm:inline">·</span>
                <span className="hidden sm:inline">0455 400 261</span>
              </div>
            </div>

            {/* Client area */}
            <div className="mt-8 border-t-2 border-white/15 pt-6" data-testid="menu-client-area">
              {client ? (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#00C2B8]">
                    Signed in · {client.name}
                  </p>
                  {CLIENT_TOOLS.map((t) => (
                    <NavLink
                      key={t.to}
                      to={t.to}
                      data-testid={`menu-client-${t.to.slice(1)}`}
                      className="font-mono text-[11px] md:text-xs uppercase tracking-widest text-white/80 hover:text-[#FFD700] transition-colors"
                    >
                      → {t.label}
                    </NavLink>
                  ))}
                  <button
                    type="button"
                    onClick={signOut}
                    data-testid="menu-client-signout"
                    className="font-mono text-[11px] md:text-xs uppercase tracking-widest text-[#FF007F] hover:text-[#FFD700] transition-colors"
                  >
                    → Log out
                  </button>
                </div>
              ) : (
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                  <Link
                    to="/login"
                    data-testid="menu-client-login"
                    className="inline-flex items-center gap-2 bg-transparent text-white border-2 border-white/60 hover:border-[#00C2B8] hover:text-[#00C2B8] rounded-full px-5 py-2.5 font-bold uppercase text-xs tracking-wide nb-press transition-colors"
                  >
                    <LockKeyhole size={14} strokeWidth={2.5} /> Client login
                  </Link>
                  <Link
                    to="/forgot-password"
                    data-testid="menu-client-forgot"
                    className="font-mono text-[11px] md:text-xs uppercase tracking-widest text-white/60 hover:text-[#00C2B8] transition-colors"
                  >
                    Forgot password →
                  </Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
